/**
 * Warm-first ordering for the Growth list.
 *
 * The list used to be strictly newest-first, which is the wrong order for the
 * job it is actually used for: writing personal notes, a handful at a time,
 * starting with the people most likely to answer. Somebody who paid, or who
 * has been checking every morning, is a warmer note than somebody who signed
 * up an hour ago and has not confirmed their email.
 *
 * The order is paid, then the people who keep checking (most check days, then
 * most recent check), then the people who checked once, then the people who
 * have not checked, then the people who never verified their email.
 *
 * Nothing here filters: every person still appears, only the order changes,
 * so the state filters above the list keep working unchanged.
 */

export type GrowthWarmth = 'paid' | 'checking' | 'checked_once' | 'no_check' | 'unverified';

/** The fields the ordering reads. A structural subset of GrowthPerson. */
export interface WarmthInput {
  state: string;
  verified: boolean;
  checks: number;
  lastCheckAt: string | null;
  createdAt: string;
}

/** Warmest first. The index is the sort key; the name is what the UI labels. */
const WARMTH_ORDER: GrowthWarmth[] = ['paid', 'checking', 'checked_once', 'no_check', 'unverified'];

export const WARMTH_LABEL: Record<GrowthWarmth, string> = {
  paid: 'Paying',
  checking: 'Checking regularly',
  checked_once: 'Checked once',
  no_check: 'No check yet',
  unverified: 'Not verified yet',
};

/**
 * Which group a person belongs to.
 *
 * Paying and unverified come from the state the API assigned; everything
 * between them is read from the check counts, so somebody who abandoned a
 * checkout after a week of daily checks is ranked by the checks, which is
 * what makes them worth writing to.
 */
export function warmth(p: WarmthInput): GrowthWarmth {
  if (p.state === 'paid') return 'paid';
  if (!p.verified || p.state === 'unverified') return 'unverified';
  const checks = Number.isFinite(p.checks) ? p.checks : 0;
  if (checks >= 2) return 'checking';
  if (checks === 1) return 'checked_once';
  return 'no_check';
}

function rank(p: WarmthInput): number {
  return WARMTH_ORDER.indexOf(warmth(p));
}

/** Milliseconds, or 0 for a missing or unparseable date, so it sorts last. */
function time(iso: string | null): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Warmest first, then most check days, then most recent check, then newest
 * account. The last tie-break keeps the order stable and predictable rather
 * than leaving equal people in whatever order the API happened to return.
 */
export function compareWarmFirst(a: WarmthInput, b: WarmthInput): number {
  const byRank = rank(a) - rank(b);
  if (byRank !== 0) return byRank;
  const checksA = Number.isFinite(a.checks) ? a.checks : 0;
  const checksB = Number.isFinite(b.checks) ? b.checks : 0;
  if (checksA !== checksB) return checksB - checksA;
  const lastA = time(a.lastCheckAt);
  const lastB = time(b.lastCheckAt);
  if (lastA !== lastB) return lastB - lastA;
  return time(b.createdAt) - time(a.createdAt);
}

/** A sorted copy. The input array is never mutated. */
export function sortWarmFirst<T extends WarmthInput>(people: readonly T[]): T[] {
  return [...people].sort(compareWarmFirst);
}

/**
 * How many people at the head of a sorted list belong to the warmest group,
 * for the "write to these first" header.
 *
 * Zero when the top group is not actually warm: with the "Not verified"
 * filter on, the top group is the coldest people in the product, and telling
 * Brian to write to them first would be wrong. Only paying people and people
 * who have done a check earn the header.
 */
export function writeFirstCount(sorted: readonly WarmthInput[]): number {
  if (sorted.length === 0) return 0;
  const top = warmth(sorted[0]);
  if (top !== 'paid' && top !== 'checking' && top !== 'checked_once') return 0;
  let n = 0;
  while (n < sorted.length && warmth(sorted[n]) === top) n += 1;
  return n;
}
